import React, { CSSProperties, Fragment, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { ArrowRight, CircleAlert } from 'lucide-react';
import { DynamicIcon, IconName } from 'lucide-react/dynamic';

import { LogoPackage } from '../sub/LogoPackage';

import { useGetSimulations } from '../../hooks/simulations';
import { useCreateSession } from '../../hooks/sessions';

import { AuthHelper } from '../../helpers/auth.helper';
import { CookiesHelper } from '../../helpers/cookies.helper';

import { ESimulationCompletionStatus, ESimulationStatus } from '../../types/entities.enum';
import { getSimulationColor } from '../../helpers/misc.helper';

export const Home = () => {

    const { mutate: createSession } = useCreateSession();
    const { data: simulations, isLoading } = useGetSimulations();

    useEffect(() => {
        const isSessionTokenValid = AuthHelper.isSessionTokenValid().result;
        if (!isSessionTokenValid) {
            createSession();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onResetSession = () => {
        CookiesHelper.remove(`${process.env.REACT_APP__SESSION_TOKEN_NAME}`);
        window.location.href = "/";
    };

    return (
        <div className='home__page'>
            <LogoPackage />

            <div className='home__top_sec'>
                <div>
                    <p className='text-4xl text-bold color--primary mb-md'>Effective Challenge Simulations</p>
                    <p className='text-lg color--gray-700'>Master difficult conversations and stakeholder management through immersive, AI-powered simulations</p>
                </div>
                <button className='btn btn--lg btn--secondary' onClick={onResetSession}>
                    Restart Entire Session
                </button>
            </div>

            <div className='card card--md d-flex gap-lg mb-2xl'>
                <div>
                    <CircleAlert className='color--info' />
                </div>
                <div>
                    <p className='text-semibold mb-sm'>Privacy Notice</p>
                    <p className='text-sm color--gray-600'>
                        Your responses during these simulations are recorded for analysis and feedback purposes. This data helps provide personalized insights to improve your communication skills. All data is processed securely and used solely for educational purposes.
                    </p>
                </div>
            </div>

            {isLoading && !simulations && (
                <div className='d-flex flex-column align-items-center justify-content-center gap-sm'>
                    <div className='list-loader' />
                    <p className='text-medium color--gray-700'>Loading simulations</p>
                </div>
            )}
            {!isLoading && !simulations?.length && (
                <p className='text-lg text-semibold color--gray-600 text-center'>No simulations found!</p>
            )}
            {!isLoading && !!simulations?.length && (
                <Fragment>
                    <p className='text-sm text-semibold color--gray-600 mb-lg tracking-wider'>AVAILABLE SIMULATIONS</p>
                    <div className='home__simulation_cards'>
                        {simulations?.map((simulation) => (
                            <Link
                                key={simulation.key}
                                to={`/simulation/${simulation.key}/stages`}
                                className='card card--md card--hover-shadow home__simulation_card'
                                style={{ '--simulation-theme-color': getSimulationColor(simulation.key) } as CSSProperties}
                            >
                                <div className='home__simulation_card__icon'>
                                    <DynamicIcon name={simulation.icon as IconName} />
                                </div>
                                <div>
                                    <div className='d-flex justify-content-between gap-sm mb-sm'>
                                        <p className='text-xl text-bold color--black'>{simulation.title}</p>
                                        <div className={`home__simulation_card__status home__simulation_card__status--${simulation.status}`}>
                                            <p className='text-sm text-medium'>{simulation.status === ESimulationStatus.AVAILABLE ? 'Available' : 'Coming Soon'}</p>
                                        </div>
                                    </div>
                                    <p className='color--gray-600'>{simulation.description}</p>
                                    <div className='home__simulation_card__bottom mt-lg'>
                                        <p className='text-medium color--primary'>{simulation.completion_status === ESimulationCompletionStatus.COMPLETED ? 'Retake' : simulation.completion_status === ESimulationCompletionStatus.IN_PROGRESS ? 'Continue' : 'Start'} Simulation</p>
                                        <ArrowRight className='w-lg h-lg color--primary' />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </Fragment>
            )}
        </div>
    );
};